/**
 * Capital gain calculations for sold fixed assets.
 * CPI / indexIncrease comes from the backend API — not recomputed here.
 */
import {
    REAL_GAIN_PERIOD_KEYS,
    getDefaultCapitalGainTaxRates,
    resolveCapitalGainTaxRates,
} from './capitalGainTaxConfig';

/** Round to nearest integer (matches Hisulit example: 293.38 → 293). */
export function roundInflationaryGain(value) {
    return Math.round(Number(value) || 0);
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

function toDateOnly(value) {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function daysInYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 366 : 365;
}

/** Matches AssetService.ClampDepreciation. */
export function clampDepreciation(amount, originalPrice) {
    const absMax = Math.abs(Number(originalPrice) || 0);
    const clamped = Math.min(Math.max(Number(amount) || 0, 0), absMax);
    return (Number(originalPrice) || 0) < 0 ? -clamped : clamped;
}

/** Form 1342 current sale-year days from 01/01 to sale date (30/360). */
export function calculateForm1342CurrentYearDays(saleDate) {
    const sale = toDateOnly(saleDate);
    if (!sale) return 0;
    return (sale.getMonth() * 30) + (sale.getDate() - 1);
}

/** 30/360 day count between two dates (Form 1342 style). */
export function calculateForm1342DaysBetween(startDate, endDate) {
    const start = toDateOnly(startDate);
    const end = toDateOnly(endDate);
    if (!start || !end) return 0;
    return (
        (end.getFullYear() - start.getFullYear()) * 360 +
        (end.getMonth() - start.getMonth()) * 30 +
        (end.getDate() - start.getDate())
    );
}

function roundShekel(value) {
    return Math.round(Number(value) || 0);
}

/**
 * Hisulit / Form 1342 depreciation at sale: prior full tax years + current-year 30/360 proration.
 */
export function calculateForm1342DepreciationAtSale({
    originalPrice,
    depreciationPercentage,
    activationDate,
    purchaseDate,
    saleDate,
}) {
    const depStart =
        toDateOnly(activationDate) || toDateOnly(purchaseDate);
    const sale = toDateOnly(saleDate);
    const absOriginal = Math.abs(Number(originalPrice) || 0);
    const sign = Number(originalPrice) < 0 ? -1 : 1;
    const rate = Number(depreciationPercentage) || 0;

    const empty = {
        previousYearsDepreciation: 0,
        currentYearDepreciation: 0,
        totalDepreciation: 0,
        remainingCost: Number(originalPrice) || 0,
        currentYearClaimedPercent: 0,
        annualDepreciationAmount: 0,
        fullPreviousYears: 0,
        currentYearDepreciationDays: 0,
        depreciationDayCountMethod: '30/360',
    };

    if (!depStart || !sale || absOriginal === 0 || rate === 0 || sale < depStart) {
        return empty;
    }

    const annualDepreciation = absOriginal * (rate / 100);
    let previousYearsDepreciationRaw = 0;
    let currentYearDepreciationRaw = 0;
    let fullPreviousYears = 0;
    let currentYearDays = 0;

    if (depStart.getFullYear() === sale.getFullYear()) {
        currentYearDays = calculateForm1342DaysBetween(depStart, sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    } else if (depStart.getMonth() === 0 && depStart.getDate() === 1) {
        fullPreviousYears = sale.getFullYear() - depStart.getFullYear();
        previousYearsDepreciationRaw = annualDepreciation * fullPreviousYears;
        currentYearDays = calculateForm1342CurrentYearDays(sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    } else {
        const endOfActivationYear = new Date(depStart.getFullYear(), 11, 31);
        const activationYearDays = calculateForm1342DaysBetween(depStart, endOfActivationYear);
        const activationYearPartialRaw = (annualDepreciation * activationYearDays) / 360;
        fullPreviousYears = Math.max(0, sale.getFullYear() - depStart.getFullYear() - 1);
        previousYearsDepreciationRaw =
            annualDepreciation * fullPreviousYears + activationYearPartialRaw;
        currentYearDays = calculateForm1342CurrentYearDays(sale);
        currentYearDepreciationRaw = (annualDepreciation * currentYearDays) / 360;
    }

    const totalDepreciationRaw = previousYearsDepreciationRaw + currentYearDepreciationRaw;
    const totalDepreciation = Math.min(roundShekel(totalDepreciationRaw), absOriginal);
    const previousYearsDepreciationDisplay = roundShekel(previousYearsDepreciationRaw);
    const currentYearDepreciationDisplay = roundShekel(currentYearDepreciationRaw);

    return {
        previousYearsDepreciationRaw: previousYearsDepreciationRaw * sign,
        currentYearDepreciationRaw: currentYearDepreciationRaw * sign,
        totalDepreciationRaw: totalDepreciationRaw * sign,
        previousYearsDepreciationDisplay: previousYearsDepreciationDisplay * sign,
        currentYearDepreciationDisplay: currentYearDepreciationDisplay * sign,
        previousYearsDepreciation: previousYearsDepreciationDisplay * sign,
        currentYearDepreciation: currentYearDepreciationDisplay * sign,
        totalDepreciation: totalDepreciation * sign,
        remainingCost: (absOriginal - totalDepreciation) * sign,
        currentYearClaimedPercent:
            absOriginal === 0
                ? 0
                : Math.round((currentYearDepreciationRaw / absOriginal) * 100 * 1e6) / 1e6,
        annualDepreciationAmount: annualDepreciation * sign,
        fullPreviousYears,
        currentYearDepreciationDays: currentYearDays,
        depreciationDayCountMethod: '30/360',
    };
}

/**
 * Legacy day-based depreciation (365/366) — fallback/debug only, not Hisulit Form 1342.
 */
export function calculateDepreciationFromActivationToDate({
    originalPrice,
    depreciationPercentage,
    activationDate,
    endDate,
}) {
    const activation = toDateOnly(activationDate);
    const end = toDateOnly(endDate);

    if (!activation || !end || end < activation) return 0;

    const annualDepreciation =
        Math.abs(Number(originalPrice) || 0) * (Number(depreciationPercentage) || 0) / 100;
    const activationYear = activation.getFullYear();
    const endYear = end.getFullYear();

    if (activationYear === endYear) {
        const daysActive = Math.floor((end - activation) / MS_PER_DAY) + 1;
        if (daysActive <= 0) return 0;
        const fraction = Math.min(1, daysActive / daysInYear(activationYear));
        return clampDepreciation(fraction * annualDepreciation, originalPrice);
    }

    const activationYearEnd = new Date(activationYear, 11, 31);
    const daysInActivationYear =
        Math.floor((activationYearEnd - activation) / MS_PER_DAY) + 1;
    const fractionActivation =
        daysInActivationYear > 0
            ? Math.min(1, daysInActivationYear / daysInYear(activationYear))
            : 0;
    const depActivationYear = fractionActivation * annualDepreciation;

    const fullYearsCount = endYear - activationYear - 1;
    const fullYearsDep = fullYearsCount > 0 ? annualDepreciation * fullYearsCount : 0;

    const startOfEndYear = new Date(endYear, 0, 1);
    const daysInEndYear = Math.floor((end - startOfEndYear) / MS_PER_DAY) + 1;
    const fractionEndYear = Math.min(1, daysInEndYear / daysInYear(endYear));
    const depLastYear = fractionEndYear * annualDepreciation;

    const total = depActivationYear + fullYearsDep + depLastYear;
    return clampDepreciation(total, originalPrice);
}

/**
 * Child enhancement depreciation at sale — uses the child's depreciation rate when set,
 * otherwise falls back to the parent rate.
 */
export function calculateChildEnhancementDepreciation({
    childOriginalPrice,
    childPurchaseDate,
    childActivationDate,
    saleDate,
    depreciationPercentage,
    parentDepreciationPercentage,
}) {
    const originalPrice = Number(childOriginalPrice) || 0;
    const childRate = depreciationPercentage ?? parentDepreciationPercentage;
    const rate = Number(childRate) || 0;
    const activation = toDateOnly(childActivationDate || childPurchaseDate);
    const sale = toDateOnly(saleDate);

    if (!originalPrice || !rate || !activation || !sale) {
        return {
            childAccumulatedDepreciation: 0,
            childRemainingOriginalCost: originalPrice,
        };
    }

    const form1342 = calculateForm1342DepreciationAtSale({
        originalPrice,
        depreciationPercentage: rate,
        activationDate: childActivationDate || childPurchaseDate,
        purchaseDate: childPurchaseDate,
        saleDate: sale,
    });

    return {
        childAccumulatedDepreciation: form1342.totalDepreciation,
        childRemainingOriginalCost: form1342.remainingCost,
    };
}

export function mapSavedCapitalGainEnhancementToChild(savedRow) {
    const enhancementAssetId =
        savedRow.enhancementAssetId ?? savedRow.EnhancementAssetId ?? savedRow.id ?? savedRow.Id;
    const originalPrice = Number(savedRow.originalPrice ?? savedRow.OriginalPrice ?? 0);
    const accumulatedDepreciationAtSale = Number(
        savedRow.accumulatedDepreciationAtSale ?? savedRow.AccumulatedDepreciationAtSale ?? 0
    );
    const remainingOriginalCostAtSale = Number(
        savedRow.remainingOriginalCostAtSale ?? savedRow.RemainingOriginalCostAtSale ?? 0
    );
    const saleDate = savedRow.saleDate ?? savedRow.SaleDate ?? null;
    const saleYear =
        saleDate != null && saleDate !== ''
            ? new Date(saleDate).getFullYear()
            : savedRow.saleYear ?? savedRow.SaleYear ?? null;

    return {
        id: enhancementAssetId,
        Id: enhancementAssetId,
        assetNumber: savedRow.assetNumber ?? savedRow.AssetNumber,
        assetName: savedRow.name ?? savedRow.Name,
        name: savedRow.name ?? savedRow.Name,
        description: savedRow.description ?? savedRow.Description,
        purchaseDate: savedRow.purchaseDate ?? savedRow.PurchaseDate,
        activationDate: savedRow.activationDate ?? savedRow.ActivationDate,
        originalPrice,
        OriginalPrice: originalPrice,
        accumulatedDepreciationAtSale,
        AccumulatedDepreciationAtSale: accumulatedDepreciationAtSale,
        remainingOriginalCost: remainingOriginalCostAtSale,
        RemainingOriginalCost: remainingOriginalCostAtSale,
        remainingOriginalCostAtSale,
        RemainingOriginalCostAtSale: remainingOriginalCostAtSale,
        saleDate,
        SaleDate: saleDate,
        saleYear,
        SaleYear: saleYear,
        depreciationPercentage:
            savedRow.depreciationPercentage ?? savedRow.DepreciationPercentage,
    };
}

export function computeSelectedEnhancementsTotals({
    selectedChildren = [],
    saleDate,
    parentDepreciationPercentage,
}) {
    return selectedChildren.reduce(
        (acc, child) => {
            const backendRemaining =
                child.remainingOriginalCostAtSale ??
                child.RemainingOriginalCostAtSale ??
                child.remainingOriginalCost ??
                child.RemainingOriginalCost;
            const backendDepreciation =
                child.accumulatedDepreciationAtSale ?? child.AccumulatedDepreciationAtSale;

            let childRemainingOriginalCost;
            let childAccumulatedDepreciation;

            if (backendRemaining != null && backendRemaining !== '') {
                childRemainingOriginalCost = Number(backendRemaining) || 0;
                childAccumulatedDepreciation =
                    backendDepreciation != null && backendDepreciation !== ''
                        ? Number(backendDepreciation) || 0
                        : Math.max(
                              0,
                              Number(child.originalPrice ?? child.OriginalPrice ?? 0) -
                                  childRemainingOriginalCost
                          );
            } else {
                const calculated = calculateChildEnhancementDepreciation({
                    childOriginalPrice: child.originalPrice ?? child.OriginalPrice,
                    childPurchaseDate: child.purchaseDate ?? child.PurchaseDate,
                    childActivationDate: child.activationDate ?? child.ActivationDate,
                    saleDate,
                    depreciationPercentage:
                        child.depreciationPercentage ?? child.DepreciationPercentage,
                    parentDepreciationPercentage,
                });
                childRemainingOriginalCost = calculated.childRemainingOriginalCost;
                childAccumulatedDepreciation = calculated.childAccumulatedDepreciation;
            }

            return {
                enhancementsBalance: acc.enhancementsBalance + childRemainingOriginalCost,
                enhancementsAccumulatedDepreciation:
                    acc.enhancementsAccumulatedDepreciation + childAccumulatedDepreciation,
            };
        },
        { enhancementsBalance: 0, enhancementsAccumulatedDepreciation: 0 }
    );
}

/** Default rates for an individual selling after 2012; use getCapitalGainTaxRatesForYear for a specific case. */
export const CAPITAL_GAIN_DEFAULT_TAX_RATES = getDefaultCapitalGainTaxRates(null, null);

export function getCapitalGainTaxRatesForYear(taxYear, caseType) {
    return getDefaultCapitalGainTaxRates(taxYear, caseType);
}

const PERIOD_CUTOFF_2003 = Date.UTC(2003, 0, 1);
const PERIOD_CUTOFF_2012 = Date.UTC(2012, 0, 1);

function toUtcDay(value) {
    if (!value) return null;
    if (typeof value === 'string') {
        const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match) return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

function emptyRealPeriods() {
    return { realUntil2002: 0, real2003To2011: 0, realFrom2012: 0, realFrom2003: 0 };
}

function withFrom2003Sum(periods) {
    return { ...periods, realFrom2003: periods.real2003To2011 + periods.realFrom2012 };
}

/**
 * Splits positive real gain linearly by holding days (section 91(ב1)):
 * until 31.12.2002, 1.1.2003 – 31.12.2011 and from 1.1.2012.
 * `realFrom2003` is the sum of the two later periods.
 */
export function splitRealGainByPeriods({ realGain, purchaseDate, saleDate }) {
    const total = Math.round(Number(realGain) || 0);
    if (total <= 0) return emptyRealPeriods();

    const start = toUtcDay(purchaseDate);
    const end = toUtcDay(saleDate);

    if (start == null || end == null || end <= start) {
        const saleBefore2003 = end != null && end < PERIOD_CUTOFF_2003;
        const saleBefore2012 = end != null && end < PERIOD_CUTOFF_2012;
        if (saleBefore2003) return withFrom2003Sum({ realUntil2002: total, real2003To2011: 0, realFrom2012: 0 });
        if (saleBefore2012) return withFrom2003Sum({ realUntil2002: 0, real2003To2011: total, realFrom2012: 0 });
        return withFrom2003Sum({ realUntil2002: 0, real2003To2011: 0, realFrom2012: total });
    }

    const overlap = (from, to) => Math.max(Math.min(end, to) - Math.max(start, from), 0);
    const totalDays = (end - start) / MS_PER_DAY;
    const untilDays = overlap(-Infinity, PERIOD_CUTOFF_2003) / MS_PER_DAY;
    const middleDays = overlap(PERIOD_CUTOFF_2003, PERIOD_CUTOFF_2012) / MS_PER_DAY;

    const realUntil2002 = Math.round((total * untilDays) / totalDays);
    const real2003To2011 = Math.round((total * middleDays) / totalDays);
    const realFrom2012 = total - realUntil2002 - real2003To2011;

    return withFrom2003Sum({ realUntil2002, real2003To2011, realFrom2012 });
}

/**
 * Computes capital gain breakdown from asset basis + API index increase.
 *
 * Cases:
 * - C: indexIncrease <= 0 — no inflationary split; real gain/loss follows totalCapitalGain only.
 * - A: totalCapitalGain <= 0 with index — inflationary amount is exempt display only; realLoss includes it; no tax.
 * - B: totalCapitalGain > 0 — inflationary amount is exempt up to total gain.
 *
 * The taxable inflationary amount (index growth until 31.12.1993, section 91(ג)) stays 0:
 * it needs the November 1993 index, which the index API does not return.
 */
export function computeCapitalGains({
    salePrice,
    originalPrice,
    accumulatedDepreciation,
    enhancementsBalance = 0,
    enhancementsAccumulatedDepreciation = 0,
    saleExpenses = 0,
    indexIncrease = 0,
    purchaseDate,
    saleDate,
}) {
    const original = Math.round(Number(originalPrice) || 0);
    const accumulated = Math.round(Number(accumulatedDepreciation) || 0);
    const enhancements = Math.round(Number(enhancementsBalance) || 0);
    const enhancementsDep = Math.round(Number(enhancementsAccumulatedDepreciation) || 0);
    const sale = Number(salePrice) || 0;
    const expenses = Number(saleExpenses) || 0;
    const idx = Number(indexIncrease) || 0;

    const remainingOriginalCost = original - accumulated + enhancements;
    const totalCapitalGain = sale - remainingOriginalCost - expenses;
    const inflationaryGain = roundInflationaryGain(remainingOriginalCost * idx);

    let inflationaryExemptGain = 0;
    let inflationaryTaxableGain = 0;
    let realGain = 0;
    let realLoss = 0;

    if (idx <= 0) {
        realGain = Math.max(totalCapitalGain, 0);
        realLoss = totalCapitalGain < 0 ? Math.abs(totalCapitalGain) : 0;
    } else if (totalCapitalGain <= 0) {
        inflationaryExemptGain = Math.max(inflationaryGain, 0);
        realLoss = Math.abs(totalCapitalGain) + inflationaryExemptGain;
    } else {
        const inflationaryAmount = Math.max(inflationaryGain, 0);
        inflationaryExemptGain = Math.min(totalCapitalGain, inflationaryAmount);
        realGain = Math.max(
            totalCapitalGain - inflationaryExemptGain - inflationaryTaxableGain,
            0
        );
    }

    const periods =
        realGain > 0
            ? splitRealGainByPeriods({ realGain, purchaseDate, saleDate })
            : emptyRealPeriods();

    return {
        remainingOriginalCost,
        enhancementsBalance: enhancements,
        enhancementsAccumulatedDepreciation: enhancementsDep,
        totalAccumulatedDepreciation: accumulated + enhancementsDep,
        totalCapitalGain,
        inflationaryGain,
        inflationaryExemptGain,
        inflationaryTaxableGain,
        realGain,
        taxableRealGain: realGain,
        ...periods,
        realLoss,
    };
}

const PERIOD_RATE_KEY = {
    realUntil2002: 'realUntil2002Rate',
    real2003To2011: 'real2003To2011Rate',
    realFrom2012: 'realFrom2012Rate',
};

function positive(value) {
    return Math.max(Number(value || 0), 0);
}

/** Real gain periods available for Section 96 replacement after current enhancements. */
export function getAvailableRealGainForReplacement(gains) {
    return REAL_GAIN_PERIOD_KEYS.reduce((sum, key) => sum + positive(gains?.[key]), 0);
}

/**
 * Order in which replacement reduces the real-gain periods: highest tax rate first,
 * later periods first on equal rates.
 */
export function getReplacementPeriodOrder(rates) {
    const effectiveRates = rates || CAPITAL_GAIN_DEFAULT_TAX_RATES;
    const byLatest = [...REAL_GAIN_PERIOD_KEYS].reverse();
    return byLatest
        .map((key, index) => ({ key, index, rate: Number(effectiveRates[PERIOD_RATE_KEY[key]] || 0) }))
        .sort((a, b) => b.rate - a.rate || a.index - b.index)
        .map((entry) => entry.key);
}

function allocateAcrossPeriods(amount, available, order) {
    let remaining = Math.max(Number(amount || 0), 0);
    const allocated = { realUntil2002: 0, real2003To2011: 0, realFrom2012: 0 };
    order.forEach((key) => {
        const part = Math.min(positive(available[key]), remaining);
        allocated[key] = part;
        remaining -= part;
    });
    return { allocated, remaining };
}

export const REPLACEMENT_WINDOW_MONTHS_BEFORE = 4;
export const REPLACEMENT_WINDOW_MONTHS_AFTER = 12;

/** Adds months keeping the day inside the target month (like .NET AddMonths: 30/06 − 4 → 29/02). */
function addMonthsClamped(date, months) {
    const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    target.setDate(Math.min(date.getDate(), lastDay));
    return target;
}

/** Section 96(א): the replacement asset must be bought 4 months before to 12 months after the sale. */
export function getReplacementPurchaseWindow(saleDate) {
    const sale = toDateOnly(saleDate);
    if (!sale) return null;
    return {
        from: addMonthsClamped(sale, -REPLACEMENT_WINDOW_MONTHS_BEFORE),
        to: addMonthsClamped(sale, REPLACEMENT_WINDOW_MONTHS_AFTER),
    };
}

export function isWithinReplacementWindow(purchaseDate, saleDate) {
    const window = getReplacementPurchaseWindow(saleDate);
    const purchase = toDateOnly(purchaseDate);
    if (!window || !purchase) return false;
    return purchase >= window.from && purchase <= window.to;
}

export function getReplacementAssetsPrice(replacementRows = []) {
    return (replacementRows || []).reduce(
        (sum, row) => sum + positive(row?.originalPrice ?? row?.OriginalPrice),
        0
    );
}

/**
 * Section 96(א) limits: the new assets' price must exceed the sold asset's remaining cost, and only
 * the excess of the sale price over that price stays taxable — the rest of the gain may be deferred.
 */
export function computeSection96Limits({ gains, salePrice, replacementRows = [] }) {
    const replacementPrice = getReplacementAssetsPrice(replacementRows);
    const soldRemainingCost = Number(gains?.remainingOriginalCost || 0);
    const priceEligible = replacementPrice > 0 && replacementPrice > soldRemainingCost;
    const taxableExcess = Math.max((Number(salePrice) || 0) - replacementPrice, 0);
    const replaceableGain =
        getAvailableRealGainForReplacement(gains) + getAvailableInflationaryGainForReplacement(gains);
    const deferrable = Math.max(
        Math.min(replaceableGain, Number(gains?.totalCapitalGain || 0) - taxableExcess),
        0
    );

    return {
        replacementPrice,
        soldRemainingCost,
        priceEligible,
        taxableExcess,
        maxDeferral: priceEligible ? deferrable : 0,
    };
}

/** Inflationary taxable gain available for Section 96 replacement after current enhancements. */
export function getAvailableInflationaryGainForReplacement(gains) {
    return Math.max(Number(gains?.inflationaryTaxableGain || 0), 0);
}

/** Whether persisted replacement totals exceed gains after enhancements changed. */
export function isReplacementInvalidAfterEnhancements(replacementData, gains) {
    const totalRealReplacementAmount = Number(replacementData?.realReplacementAmount || 0);
    const totalInflationaryReplacementAmount = Number(
        replacementData?.inflationaryReplacementAmount || 0
    );

    return (
        totalRealReplacementAmount > getAvailableRealGainForReplacement(gains) ||
        totalInflationaryReplacementAmount > getAvailableInflationaryGainForReplacement(gains)
    );
}

/**
 * Splits a single real replacement amount across the three real-gain periods, highest rate first.
 * Clamps the total to the available real gain so display/tax never exceed the taxable periods.
 */
export function splitRealReplacementAmount({
    realReplacementAmount,
    realUntil2002,
    real2003To2011,
    realFrom2012,
    rates,
}) {
    const available = { realUntil2002, real2003To2011, realFrom2012 };
    const capped = Math.min(
        positive(realReplacementAmount),
        getAvailableRealGainForReplacement(available)
    );
    const { allocated } = allocateAcrossPeriods(capped, available, getReplacementPeriodOrder(rates));

    return {
        replacementUntil2002: allocated.realUntil2002,
        replacement2003To2011: allocated.real2003To2011,
        replacementFrom2012: allocated.realFrom2012,
        replacementFrom2003: allocated.real2003To2011 + allocated.realFrom2012,
        unappliedReplacement: positive(realReplacementAmount) - capped,
    };
}

/**
 * @param {number} [params.salePrice] - when given, the Section 96 limit (see computeSection96Limits) caps the total.
 */
export function buildReplacementSummary({ replacementData, gains, rates, salePrice }) {
    let safeRealReplacementAmount = Math.min(
        positive(replacementData?.realReplacementAmount),
        getAvailableRealGainForReplacement(gains)
    );
    let safeInflationaryReplacementAmount = Math.min(
        positive(replacementData?.inflationaryReplacementAmount),
        getAvailableInflationaryGainForReplacement(gains)
    );

    if (salePrice != null && (safeRealReplacementAmount > 0 || safeInflationaryReplacementAmount > 0)) {
        const { maxDeferral } = computeSection96Limits({
            gains,
            salePrice,
            replacementRows: replacementData?.selectedAssets,
        });
        safeRealReplacementAmount = Math.min(safeRealReplacementAmount, maxDeferral);
        safeInflationaryReplacementAmount = Math.min(
            safeInflationaryReplacementAmount,
            maxDeferral - safeRealReplacementAmount
        );
    }

    const split = splitRealReplacementAmount({
        realReplacementAmount: safeRealReplacementAmount,
        realUntil2002: gains?.realUntil2002,
        real2003To2011: gains?.real2003To2011,
        realFrom2012: gains?.realFrom2012,
        rates,
    });

    return {
        realReplacementAmount: safeRealReplacementAmount,
        inflationaryReplacementAmount: safeInflationaryReplacementAmount,
        ...split,
        unappliedReplacement: 0,
    };
}

/**
 * Build API save rows with per-row real-gain splits. The stored columns keep the
 * from-2003 / until-2002 shape; the three-period split is recomputed on load.
 */
export function buildReplacementSaveRows(replacementRows = [], gains = {}, rates) {
    const order = getReplacementPeriodOrder(rates);
    const available = {
        realUntil2002: positive(gains?.realUntil2002),
        real2003To2011: positive(gains?.real2003To2011),
        realFrom2012: positive(gains?.realFrom2012),
    };

    return replacementRows.map((row) => {
        const real = Number(row.realReplacementAmount || 0);
        const { allocated } = allocateAcrossPeriods(real, available, order);
        REAL_GAIN_PERIOD_KEYS.forEach((key) => {
            available[key] -= allocated[key];
        });

        return {
            replacementAssetId: row.assetId,
            realReplacementAmount: real,
            inflationaryReplacementAmount: Number(row.inflationaryReplacementAmount || 0),
            replacementFrom2003: allocated.real2003To2011 + allocated.realFrom2012,
            replacementUntil2002: allocated.realUntil2002,
        };
    });
}

export function getBeforeReplacementCost(row) {
    const remaining = row?.remainingOriginalCost ?? row?.remainingCost;
    if (remaining != null && remaining !== '') {
        return Number(remaining) || 0;
    }
    return Number(row?.originalPrice ?? 0);
}

export function computeAdjustedGainBeforeTax(gains, adjustedGains) {
    if (!gains || !adjustedGains) return 0;
    return (
        Number(adjustedGains.adjustedInflationaryTaxableGain || 0) +
        Number(adjustedGains.adjustedRealUntil2002 || 0) +
        Number(adjustedGains.adjustedReal2003To2011 || 0) +
        Number(adjustedGains.adjustedRealFrom2012 || 0) +
        positive(gains.inflationaryExemptGain) -
        positive(gains.realLoss)
    );
}

export function mapSavedReplacementToRow(savedRow, context = {}) {
    const originalPrice = Number(
        savedRow.replacementAssetOriginalPrice ??
            savedRow.ReplacementAssetOriginalPrice ??
            savedRow.replacementAssetOriginalCostBefore ??
            savedRow.ReplacementAssetOriginalCostBefore ??
            0
    );
    const remainingCost = Number(
        savedRow.replacementAssetOriginalCostBefore ??
            savedRow.ReplacementAssetOriginalCostBefore ??
            0
    );

    const asset = {
        id: savedRow.replacementAssetId ?? savedRow.ReplacementAssetId,
        assetNumber: savedRow.replacementAssetNumber ?? savedRow.ReplacementAssetNumber,
        name: savedRow.replacementAssetName ?? savedRow.ReplacementAssetName,
        purchaseDate: savedRow.replacementAssetPurchaseDate ?? savedRow.ReplacementAssetPurchaseDate,
        originalPrice,
        remainingOriginalCost: remainingCost,
        realReplacementAmount:
            savedRow.realReplacementAmount ?? savedRow.RealReplacementAmount,
        inflationaryReplacementAmount:
            savedRow.inflationaryReplacementAmount ?? savedRow.InflationaryReplacementAmount,
        replacementFrom2003: savedRow.replacementFrom2003 ?? savedRow.ReplacementFrom2003,
        replacementUntil2002: savedRow.replacementUntil2002 ?? savedRow.ReplacementUntil2002,
    };

    const mapped = mapAssetToReplacementRow(asset, context);
    return {
        ...mapped,
        id: savedRow.id ?? savedRow.Id ?? null,
        replacementId: savedRow.id ?? savedRow.Id ?? null,
        originalPrice: originalPrice || mapped.originalPrice,
        remainingCost: remainingCost || mapped.remainingCost,
        replacementFrom2003: Number(asset.replacementFrom2003 || 0),
        replacementUntil2002: Number(asset.replacementUntil2002 || 0),
    };
}

export function mapSavedReplacementsToReplacementData(savedRows = [], context = {}) {
    const selectedAssets = (savedRows || []).map((row) => mapSavedReplacementToRow(row, context));
    const totals = computeReplacementTotals(selectedAssets);
    return {
        selectedAssets,
        realReplacementAmount: totals.totalRealReplacementAmount,
        inflationaryReplacementAmount: totals.totalInflationaryReplacementAmount,
    };
}

export function hasAppliedReplacement(replacementSummary) {
    return (
        Number(replacementSummary?.realReplacementAmount || 0) > 0 ||
        Number(replacementSummary?.inflationaryReplacementAmount || 0) > 0
    );
}

/** Whether any taxable/real period exists for Section 96 replacement. */
export function hasReplaceableTaxableGain(gains) {
    if (!gains) return false;
    return (
        getAvailableRealGainForReplacement(gains) > 0 ||
        Number(gains.inflationaryTaxableGain || 0) > 0
    );
}

export function computeAdjustedGainsAfterReplacement(gains, replacementSummary) {
    const reduce = (gainKey, replacementKey) =>
        Math.max(Number(gains?.[gainKey] || 0) - Number(replacementSummary?.[replacementKey] || 0), 0);

    const adjustedRealUntil2002 = gains ? reduce('realUntil2002', 'replacementUntil2002') : 0;
    const adjustedReal2003To2011 = gains ? reduce('real2003To2011', 'replacement2003To2011') : 0;
    const adjustedRealFrom2012 = gains ? reduce('realFrom2012', 'replacementFrom2012') : 0;

    return {
        adjustedRealUntil2002,
        adjustedReal2003To2011,
        adjustedRealFrom2012,
        adjustedRealFrom2003: adjustedReal2003To2011 + adjustedRealFrom2012,
        adjustedInflationaryTaxableGain: gains
            ? reduce('inflationaryTaxableGain', 'inflationaryReplacementAmount')
            : 0,
    };
}

export function getReplacementReductionAsOfDate(asset, saleDate) {
    const activation = toDateOnly(asset?.activationDate ?? asset?.ActivationDate ?? asset?.purchaseDate ?? asset?.PurchaseDate);
    const sale = toDateOnly(saleDate);
    if (!sale) return saleDate;
    if (!activation || sale >= activation) return saleDate;

    // Not yet active at sale — if it activates later in the same tax year, evaluate at year-end.
    if (activation.getFullYear() === sale.getFullYear()) {
        return new Date(sale.getFullYear(), 11, 31);
    }

    return saleDate;
}

export function getAssetReductionBalanceValues(asset, { saleDate, parentDepreciationPercentage } = {}) {
    const backendRemaining = asset?.remainingOriginalCost ?? asset?.RemainingOriginalCost;
    const backendDepreciation =
        asset?.accumulatedDepreciationAtSale ?? asset?.AccumulatedDepreciationAtSale;
    const originalPrice = Number(asset?.originalPrice ?? asset?.OriginalPrice ?? 0);

    if (backendRemaining != null && backendRemaining !== '') {
        const remainingOriginalCost = Number(backendRemaining) || 0;
        const accumulatedDepreciation =
            backendDepreciation != null && backendDepreciation !== ''
                ? Number(backendDepreciation) || 0
                : Math.max(0, originalPrice - remainingOriginalCost);
        return { remainingOriginalCost, accumulatedDepreciation };
    }

    const depreciationPercentage =
        asset?.depreciationPercentage ?? asset?.DepreciationPercentage ?? parentDepreciationPercentage;
    const evaluationDate = getReplacementReductionAsOfDate(asset, saleDate);

    const calculated = calculateChildEnhancementDepreciation({
        childOriginalPrice: originalPrice,
        childPurchaseDate: asset?.purchaseDate ?? asset?.PurchaseDate,
        childActivationDate: asset?.activationDate ?? asset?.ActivationDate,
        saleDate: evaluationDate,
        depreciationPercentage,
        parentDepreciationPercentage,
    });

    return {
        remainingOriginalCost: calculated.childRemainingOriginalCost,
        accumulatedDepreciation: calculated.childAccumulatedDepreciation,
    };
}

export function getAssetId(asset) {
    return asset?.assetId ?? asset?.id ?? asset?.Id;
}

export function getAssetYearFromDate(purchaseDate, fallbackYear) {
    if (purchaseDate) {
        const d = new Date(purchaseDate);
        if (!Number.isNaN(d.getTime())) return d.getFullYear();
    }
    const year = Number(fallbackYear);
    return Number.isFinite(year) ? year : null;
}

/** Row basis for replacement clamping: remaining cost before replacement. */
export function getReplacementRowMaxBasis(row) {
    return getBeforeReplacementCost(row);
}

export function mapAssetToReplacementRow(asset, context = {}) {
    const purchaseDate = asset?.purchaseDate || asset?.PurchaseDate;
    const originalPrice = Number(asset?.originalPrice ?? asset?.OriginalPrice ?? 0);
    const { remainingOriginalCost } = context?.saleDate
        ? getAssetReductionBalanceValues(asset, context)
        : {
              remainingOriginalCost: Number(
                  asset?.remainingOriginalCost ??
                      asset?.RemainingOriginalCost ??
                      asset?.remainingCost ??
                      0
              ),
          };

    return {
        assetId: getAssetId(asset),
        assetNumber: asset?.assetNumber ?? asset?.AssetNumber ?? '',
        assetName: asset?.assetName ?? asset?.AssetName ?? asset?.name ?? asset?.Name ?? '',
        purchaseDate,
        year: getAssetYearFromDate(purchaseDate, asset?.year ?? asset?.Year),
        originalPrice,
        remainingCost: remainingOriginalCost,
        realReplacementAmount: Number(asset?.realReplacementAmount || 0),
        inflationaryReplacementAmount: Number(asset?.inflationaryReplacementAmount || 0),
    };
}

/** After replacement = לפני שחלוף נוכחי minus row replacement amounts (Hisulit). */
export function computeAfterReplacementCost(row) {
    const beforeBasis = getBeforeReplacementCost(row);
    const totalReplacement =
        Number(row?.realReplacementAmount || 0) + Number(row?.inflationaryReplacementAmount || 0);
    return Math.max(beforeBasis - totalReplacement, 0);
}

export function computeReplacementTotals(replacementRows = []) {
    const totalRealReplacementAmount = replacementRows.reduce(
        (sum, row) => sum + Number(row.realReplacementAmount || 0),
        0
    );
    const totalInflationaryReplacementAmount = replacementRows.reduce(
        (sum, row) => sum + Number(row.inflationaryReplacementAmount || 0),
        0
    );

    return {
        totalRealReplacementAmount,
        totalInflationaryReplacementAmount,
        totalReplacement: totalRealReplacementAmount + totalInflationaryReplacementAmount,
    };
}

/**
 * Clamp a row replacement field against row basis and available gain buckets.
 */
export function clampReplacementRowField({
    row,
    field,
    rawValue,
    replacementRows,
    maxRealGain,
    maxInflationaryGain,
    maxTotalDeferral = Infinity,
}) {
    const assetId = getAssetId(row);
    const requested = Number(rawValue);
    let errorCode = null;

    if (Number.isNaN(requested) || requested < 0) {
        return {
            realReplacementAmount: Number(row.realReplacementAmount || 0),
            inflationaryReplacementAmount: Number(row.inflationaryReplacementAmount || 0),
            errorCode: 'replacementNegative',
        };
    }

    const value = Math.max(requested, 0);

    const otherRealTotal = replacementRows.reduce((sum, r) => {
        if (getAssetId(r) === assetId) return sum;
        return sum + Number(r.realReplacementAmount || 0);
    }, 0);

    const otherInflationaryTotal = replacementRows.reduce((sum, r) => {
        if (getAssetId(r) === assetId) return sum;
        return sum + Number(r.inflationaryReplacementAmount || 0);
    }, 0);

    const beforeBasis = getBeforeReplacementCost(row);
    const currentReal =
        field === 'realReplacementAmount' ? value : Number(row.realReplacementAmount || 0);
    const currentInflationary =
        field === 'inflationaryReplacementAmount'
            ? value
            : Number(row.inflationaryReplacementAmount || 0);

    let real = currentReal;
    let inflationary = currentInflationary;

    if (field === 'realReplacementAmount') {
        const maxAllowed = Math.max(maxRealGain - otherRealTotal, 0);
        if (value > maxAllowed) errorCode = 'realExceedsGain';
        real = Math.min(value, maxAllowed);
    } else {
        const maxAllowed = Math.max(maxInflationaryGain - otherInflationaryTotal, 0);
        if (value > maxAllowed) errorCode = 'inflationaryExceedsGain';
        inflationary = Math.min(value, maxAllowed);
    }

    const section96Overflow = otherRealTotal + otherInflationaryTotal + real + inflationary - maxTotalDeferral;
    if (section96Overflow > 0) {
        errorCode = 'exceedsSection96Limit';
        if (field === 'realReplacementAmount') {
            real = Math.max(real - section96Overflow, 0);
        } else {
            inflationary = Math.max(inflationary - section96Overflow, 0);
        }
    }

    const rowTotal = real + inflationary;
    if (rowTotal > beforeBasis) {
        errorCode = 'exceedsBeforeBasis';
        const overflow = rowTotal - beforeBasis;
        if (field === 'realReplacementAmount') {
            real = Math.max(real - overflow, 0);
        } else {
            inflationary = Math.max(inflationary - overflow, 0);
        }
    }

    return {
        realReplacementAmount: real,
        inflationaryReplacementAmount: inflationary,
        errorCode,
    };
}

/**
 * Tax per column: taxable inflationary amount and each real-gain period at its own rate.
 * Never taxes the exempt inflationary amount. When totalCapitalGain <= 0, all tax amounts are zero.
 */
export function computeTaxSummary({
    totalCapitalGain,
    inflationaryTaxableGain = 0,
    realUntil2002 = 0,
    real2003To2011 = 0,
    realFrom2012 = 0,
    inflationaryTaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.inflationaryTaxableRate,
    realUntil2002TaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.realUntil2002Rate,
    real2003To2011TaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.real2003To2011Rate,
    realFrom2012TaxRate = CAPITAL_GAIN_DEFAULT_TAX_RATES.realFrom2012Rate,
    advancePaid = 0,
}) {
    const isCapitalLoss = Number(totalCapitalGain) <= 0;
    const safe = (value) => (isCapitalLoss ? 0 : positive(value));
    const taxOn = (amount, rate) => (amount * (Number(rate) || 0)) / 100;

    const safeInflationaryTaxableGain = safe(inflationaryTaxableGain);
    const safeRealUntil2002 = safe(realUntil2002);
    const safeReal2003To2011 = safe(real2003To2011);
    const safeRealFrom2012 = safe(realFrom2012);

    const taxOnInflationary = taxOn(safeInflationaryTaxableGain, inflationaryTaxRate);
    const taxOnRealUntil2002 = taxOn(safeRealUntil2002, realUntil2002TaxRate);
    const taxOnReal2003To2011 = taxOn(safeReal2003To2011, real2003To2011TaxRate);
    const taxOnRealFrom2012 = taxOn(safeRealFrom2012, realFrom2012TaxRate);
    const totalTax = taxOnInflationary + taxOnRealUntil2002 + taxOnReal2003To2011 + taxOnRealFrom2012;
    const balanceDue = Math.max(totalTax - (Number(advancePaid) || 0), 0);

    return {
        inflationaryTaxableGain: safeInflationaryTaxableGain,
        realUntil2002: safeRealUntil2002,
        real2003To2011: safeReal2003To2011,
        realFrom2012: safeRealFrom2012,
        realFrom2003: safeReal2003To2011 + safeRealFrom2012,
        taxOnInflationary,
        taxOnRealUntil2002,
        taxOnReal2003To2011,
        taxOnRealFrom2012,
        taxOnRealFrom2003: taxOnReal2003To2011 + taxOnRealFrom2012,
        totalTax,
        balanceDue,
    };
}

function getSaleYear(saleDate) {
    const day = toUtcDay(saleDate);
    return day == null ? null : new Date(day).getUTCFullYear();
}

/**
 * Full capital-gain result for one sold asset, shared by the calculation modal,
 * the sold-assets list and the annual-report table.
 *
 * @param {object} params
 * @param {object} params.basis - GetCapitalGainDepreciationAtSale data.
 * @param {object} params.cpi - GetCapitalGainIndexCalculation data.
 * @param {object[]} [params.enhancements] - selected enhancement children.
 * @param {object} [params.replacements] - { selectedAssets, realReplacementAmount, inflationaryReplacementAmount }.
 * @param {object} [params.inputs] - { salePrice, saleExpenses, rates, advancePaid }.
 * @param {string} [params.caseType]
 */
export function buildCapitalGainResult({
    basis,
    cpi,
    enhancements = [],
    replacements = null,
    inputs = {},
    caseType,
}) {
    if (!basis || !cpi) return null;

    const saleDate = basis.sellDate ?? basis.SellDate ?? null;
    const purchaseDate = basis.purchaseDate ?? basis.PurchaseDate ?? null;
    const saleYear = getSaleYear(saleDate);
    const parentDepreciationPercentage =
        basis.parentDepreciationPercentage ?? basis.ParentDepreciationPercentage ?? 0;

    const { enhancementsBalance, enhancementsAccumulatedDepreciation } =
        computeSelectedEnhancementsTotals({
            selectedChildren: enhancements || [],
            saleDate,
            parentDepreciationPercentage,
        });

    const hasSalePriceInput = inputs.salePrice != null && inputs.salePrice !== '';
    const salePrice = Number(hasSalePriceInput ? inputs.salePrice : basis.sellAmount ?? 0) || 0;
    const saleExpenses = Number(inputs.saleExpenses || 0) || 0;
    const advancePaid = Number(inputs.advancePaid || 0) || 0;
    const rates = resolveCapitalGainTaxRates(inputs.rates, saleYear, caseType);

    const gains = computeCapitalGains({
        salePrice,
        originalPrice: basis.originalPrice,
        accumulatedDepreciation: basis.accumulatedDepreciationAtSale,
        enhancementsBalance,
        enhancementsAccumulatedDepreciation,
        saleExpenses,
        indexIncrease: cpi.indexIncrease,
        purchaseDate,
        saleDate,
    });

    const replacementSummary = buildReplacementSummary({
        replacementData: replacements,
        gains,
        rates,
        salePrice,
    });
    const adjustedGains = computeAdjustedGainsAfterReplacement(gains, replacementSummary);
    const gainBeforeTax = computeAdjustedGainBeforeTax(gains, adjustedGains);

    const tax = computeTaxSummary({
        totalCapitalGain: gains.totalCapitalGain,
        inflationaryTaxableGain: adjustedGains.adjustedInflationaryTaxableGain,
        realUntil2002: adjustedGains.adjustedRealUntil2002,
        real2003To2011: adjustedGains.adjustedReal2003To2011,
        realFrom2012: adjustedGains.adjustedRealFrom2012,
        inflationaryTaxRate: rates.inflationaryTaxableRate,
        realUntil2002TaxRate: rates.realUntil2002Rate,
        real2003To2011TaxRate: rates.real2003To2011Rate,
        realFrom2012TaxRate: rates.realFrom2012Rate,
        advancePaid,
    });

    return {
        saleDate,
        saleYear,
        salePrice,
        saleExpenses,
        advancePaid,
        rates,
        enhancementsBalance,
        enhancementsAccumulatedDepreciation,
        gains,
        replacementSummary,
        adjustedGains,
        gainBeforeTax,
        tax,
    };
}
